import { useEffect, useState } from "react";
import {
  ActionIcon,
  Button,
  Group,
  Table,
  Modal,
  Text,
  Stack,
  Loader,
  Badge,
} from "@mantine/core";
import { IconPencil, IconTrash, IconSearch } from "@tabler/icons-react";
import { Link } from "wouter";
import { getMcpServers, deleteMcpServer } from "./mcpServerApi";
import { notifications } from "@mantine/notifications";
import McpToolDiscovery from "./McpToolDiscovery";

export default function McpServerList() {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState(null);
  const [discoveryServer, setDiscoveryServer] = useState(null);

  const fetchServers = async () => {
    setLoading(true);
    try {
      const data = await getMcpServers();
      setServers(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServers();
  }, []);

  const confirmDelete = async () => {
    if (!deleteModal) return;
    try {
      await deleteMcpServer(deleteModal.id);
      setDeleteModal(null);
      fetchServers();
    } catch (err) {
      notifications.show({
        title: "Error",
        message: err.message,
        color: "red",
      });
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      <Modal
        opened={deleteModal != null}
        onClose={() => setDeleteModal(null)}
        title="Delete MCP Server"
      >
        {deleteModal && (
          <Stack>
            <Text size="sm">
              Are you sure you want to delete{" "}
              <Text span fw={700}>
                {deleteModal.name}
              </Text>
              ?
            </Text>
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setDeleteModal(null)}>
                Cancel
              </Button>
              <Button color="red" onClick={confirmDelete}>
                Delete
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      <McpToolDiscovery
        server={discoveryServer}
        onClose={() => setDiscoveryServer(null)}
      />

      <Group justify="space-between">
        <h2>MCP Servers</h2>
        <Button component={Link} to="/mcp-servers/new" variant="outline">
          Add Server
        </Button>
      </Group>

      {servers.length === 0 ? (
        <Text c="dimmed">
          No MCP servers configured. Add a server to discover and import tools.
        </Text>
      ) : (
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>URL</Table.Th>
              <Table.Th>Tools</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {servers.map((server) => (
              <Table.Tr key={server.id}>
                <Table.Td>{server.name}</Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed" truncate="end" maw={300}>
                    {server.url}
                  </Text>
                </Table.Td>
                <Table.Td>
                  {server.discoveredToolCount != null ? (
                    <Badge variant="light" color="teal">
                      {server.discoveredToolCount} tools
                    </Badge>
                  ) : (
                    <Text size="sm" c="dimmed">
                      Not scanned
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Group wrap="nowrap" gap="xs">
                    <ActionIcon
                      variant="light"
                      color="teal"
                      title="Discover tools"
                      onClick={() => setDiscoveryServer(server)}
                    >
                      <IconSearch size={16} />
                    </ActionIcon>
                    <ActionIcon
                      variant="default"
                      component={Link}
                      to={`/mcp-servers/${server.id}`}
                    >
                      <IconPencil size={16} />
                    </ActionIcon>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={() => setDeleteModal(server)}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </div>
  );
}
